import styles from "./styles/SchoolFeedDisplay.module.scss";
import classNames from "classnames";
import {
  IFallbackData,
  ISchoolFeedUrlQueries,
  isSchoolBlocksApp,
  isSchoolFeedStandalone,
} from "../../utils/SchoolBlocksUtilities";
import { useStores } from "../../stores/useStores";
import { Suspense } from "react";
import SchoolFeedNewsDisplay, {
  SchoolFeedNewsLoading,
} from "./SchoolFeedNewsDisplay";
import SchoolFeedFilesDisplay from "./SchoolFeedFilesDisplay";
import { SchoolFeedMediaDisplay } from "./SchoolFeedMediaDisplay";
import SchoolFeedCourseworkDisplay from "./SchoolFeedCourseworkDisplay";
import {
  currentDateFormat,
  currentMonthFormat,
} from "../utilities/CleverCalendar/utils";
import moment from "moment";
import { useSchoolFeedContentPathQueryParams } from "./hooks/useSchoolFeedInfiniteScroll";
import { currentRangeUrlQueryFormat, getEventsForRange } from "./utils";
import { SchoolFeedListCalendar } from "./SchoolFeedMonthCalendar";
import { ListCalendar } from "../utilities/CleverCalendar/ListCalendar";
import WatsonApi from "../../backends/WatsonApi";
import Leaderboard from "./leaderboard/Leaderboard";
import { ErrorBoundary } from "../utilities/rollbar/ErrorBoundary";

export const useInitialNewsPromise = (
  urlQuery,
): Promise<{
  contentItems: NewsContentItem[];
  total: number;
}> => {
  const pathQueryParams = useSchoolFeedContentPathQueryParams();

  const q = {
    expand: "organization,discussion_prompt",
    page: 1,
    is_news: true,
    page_size: 10,
    ...pathQueryParams,
  };

  return new Promise((res) => {
    WatsonApi().then((client) => {
      let method;
      if (urlQuery.orgId) {
        method = client.apis.organizations.organizations_schoolfeed_items_list;
      } else {
        method = client.apis.schoolfeed_items.schoolfeed_items_list;
      }

      method({
        organization_pk: urlQuery.orgId,
        ...q,
      }).then((result) => {
        res({
          contentItems: result.obj.data,
          total: result.obj.meta.total,
        });
      });
    });
  });
};

export function getContactCardDataPromise(orgId: string): Promise<any> {
  return new Promise(async (res, rej) => {
    if (!orgId) {
      rej(new Error("Organization ID is required"));
      return;
    }

    try {
      const client = await WatsonApi();
      const result = await client.apis.organizations.organizations_read({
        id: orgId,
        expand: ["detail", "locations", "categories", "third_party_links"],
      });
      res(result.body);
    } catch (error) {
      rej(error);
    }
  });
}

export function useContributingOrganizationsCountPromise(): Promise<number> | null {
  const { organizationStore } = useStores();

  const isSchoolBlocks = isSchoolBlocksApp();

  if (!isSchoolBlocks) {
    return null;
  }

  return new Promise(async (res) => {
    WatsonApi().then((client) => {
      client.apis.organizations
        .organizations_cleverfeed_directory({
          id: organizationStore.organization.id,
          fields: ["path", "sort_order", "category_name"], // slight optimization since all we need is length
        })
        .then((result) => {
          const parsed = JSON.parse(result.data);
          res(parsed.blocks.length);
        });
    });
  });
}

export const useGetInitialPromises = (urlQuery) => {
  const { interfaceStore } = useStores();
  const pathQueryParams = useSchoolFeedContentPathQueryParams();

  let initialEventsPromise, initialNewsPromise;
  // Note that this is a server component - this breakpoint is NOT RESPONSIVE! This is the server's expectation
  // of screen size based on user agent.
  const showWidgets = interfaceStore.breakpoint === "break-point-lg";

  if (showWidgets || urlQuery.type === "news") {
    initialNewsPromise = useInitialNewsPromise(urlQuery);
  }
  if (showWidgets || urlQuery.type === "events") {
    const selectedDateMoment = urlQuery.currentSelectedDate
      ? moment(urlQuery.currentSelectedDate)
      : moment();
    initialEventsPromise = getEventsForRange(
      pathQueryParams,
      urlQuery,
      selectedDateMoment.startOf("month").format(currentRangeUrlQueryFormat),
      selectedDateMoment
        .add(1, "months")
        .startOf("month")
        .format(currentRangeUrlQueryFormat),
    );
  }

  return { initialNewsPromise, initialEventsPromise };
};

export function SchoolFeedContent(props: {
  urlQuery: ISchoolFeedUrlQueries;
  initialNewsPromise?: Promise<IFallbackData>;
  initialEventsPromise?: Promise<IFallbackData>;
}) {
  if (props.urlQuery.currentView === "leaderboard") {
    return <Leaderboard />;
  }
  switch (props.urlQuery.type) {
    case "news":
      // @ts-ignore
      return (
        <Suspense fallback={<SchoolFeedNewsLoading />}>
          <SchoolFeedNewsDisplay
            urlQuery={props.urlQuery}
            initialNewsPromise={props.initialNewsPromise}
          />
        </Suspense>
      );
    case "events":
      return (
        <Suspense
          fallback={
            // @ts-ignore
            <ListCalendar
              currentMonth={moment().format(currentMonthFormat)}
              currentDate={moment().format(currentDateFormat)}
              events={[]}
              isLoading={true}
            />
          }
        >
          <SchoolFeedListCalendar
            initialEventsPromise={props.initialEventsPromise}
          />
        </Suspense>
      );
    case "files":
      return <SchoolFeedFilesDisplay urlQuery={props.urlQuery} />;
    case "media":
      return <SchoolFeedMediaDisplay />;
    case "coursework":
      return <SchoolFeedCourseworkDisplay urlQuery={props.urlQuery} />;
  }
}

const SchoolFeedDisplay = (props: { urlQuery: ISchoolFeedUrlQueries }) => {
  const displayContainerClassName = classNames({
    [styles.displayContainer]: true,
    [styles.displayContainerStandalone]: isSchoolFeedStandalone(),
    [styles.displayContainerFiles]: props.urlQuery.type === "files",
  });

  const { initialNewsPromise, initialEventsPromise } = useGetInitialPromises(
    props.urlQuery,
  );

  return (
    <div
      className={displayContainerClassName}
      data-current-view={props.urlQuery.currentView}
    >
      <ErrorBoundary>
        <SchoolFeedContent
          key={props.urlQuery.type} // Keep key to force remount and show fallback during transitions
          urlQuery={props.urlQuery}
          initialEventsPromise={initialEventsPromise}
          initialNewsPromise={initialNewsPromise}
        />
      </ErrorBoundary>
    </div>
  );
};

export default SchoolFeedDisplay;
